import React, { useEffect } from "react";
import { FormProvider } from "react-hook-form";
import DatePicker from "components/DatePicker/DatePicker";
import { UploadFormRow } from "components/Inputs";
import { Input } from "components/ui/input";
import Button from "components/Buttons";
import { gql, useQuery } from "@apollo/client";
import {
  LIST_DISCIPLINES,
  LIST_DISCIPLINES_QUERY_OPTIONS,
} from "apollo/queries/disciplines";
import { formatListArray } from "utils";
import { useModalStore, Modal as ModalKey } from "stores/modal/useModalStore";
import MultiSelectField from "components/Checkbox/HorseProfile/HorseMultiSelect";

const HorseForm = ({
  onSubmit,
  onCancel,
  isLoading,
  methods,
  defaultImage,
}) => {
  const { register, formState } = methods
  const current = useModalStore((s) => s.current);
  const isEditHorse = current === ModalKey.EditHorse;
  const {
    data: listDisciplinesData,
    refetch: refetchListDisciplines,
    loading: disciplinesLoading,
    error: disciplinesError,
  } = useQuery(gql(LIST_DISCIPLINES), LIST_DISCIPLINES_QUERY_OPTIONS);

  const disciplinesOptions = formatListArray(
    listDisciplinesData?.listDisciplines
  );

  const westernOptions = disciplinesOptions.filter(
    (option) => String(option?.label ?? "").toUpperCase() === "WESTERN"
  );

  useEffect(() => {
    const fetchDisciplinesData = async () => {
      try {
        await refetchListDisciplines();
      } catch { /* empty */ }
    };
    fetchDisciplinesData();
    // Refetch once on mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <div className="flex flex-col items-center gap-y-[25px]">
          <div className="flex justify-center gap-6 mt-[25px]">
            <div className="flex flex-col items-center w-auto gap-y-[30px]">

              <div>
                <Input
                  name="name"
                  label="Horse Name:"
                  placeholder="Enter Your Horse Name Here"
                  className="horse-profile-form__field w-[210px] sm:!w-[340px]"
                  {...register("name")}
                />
                {formState?.errors.name && (
                  <p className="text-red-500 text-sm font-montserrat mt-1">
                    {formState?.errors.name?.message}
                  </p>
                )}
              </div>

              <div>
                <DatePicker
                  name="dob"
                  label={"Date of Birth"}
                  placeholder={"Select Your Horse’s DOB"}
                  required
                  className="!w-[210px] sm:!w-[340px]"
                  methods={methods}
                />
                {formState?.errors.dob && (
                  <p className="text-red-500 text-sm font-montserrat mt-1">
                    {formState?.errors.dob?.message}
                  </p>
                )}
              </div>
              <div>
                <MultiSelectField
                  name="discipline"
                  label="Discipline:"
                  control={methods?.control}
                  className="horse-profile-form__field w-[210px] sm:!w-[340px]"
                  placeholder={
                    disciplinesLoading
                      ? "Loading disciplines…"
                      : "Select discipline"
                  }
                  options={westernOptions}
                  disabled={disciplinesLoading}
                  stylesOverride={{
                    dropdownIndicator: (base) => ({ ...base, marginBottom: "0px" }),
                    clearIndicator: (base) => ({ ...base, marginBottom: "0px" }),
                  }}
                />
                {disciplinesError &&
                  !disciplinesLoading &&
                  westernOptions.length === 0 && (
                  <p className="text-red-500 text-sm font-montserrat mt-1">
                    Disciplines could not be loaded. If this persists, confirm
                    the API environment matches this app (listDisciplines /
                    Discipline data source).
                  </p>
                )}
                {!disciplinesLoading &&
                  !disciplinesError &&
                  westernOptions.length === 0 &&
                  Array.isArray(listDisciplinesData?.listDisciplines) &&
                  listDisciplinesData.listDisciplines.length > 0 && (
                    <p className="text-amber-600 text-sm font-montserrat mt-1">
                      No western disciplines were returned for this environment.
                      Check DisciplineTable rows use group WESTERN (any casing).
                    </p>
                  )}
                {formState?.errors?.discipline && (
                  <p className="text-red-500 text-sm font-montserrat mt-1">
                    {formState?.errors?.discipline?.message}
                  </p>
                )}
              </div>

              <Input
                readOnly={isEditHorse}
                className="horse-profile-form__field w-[210px] sm:!w-[340px]"
                label="Horse Registered Name:"
                name="registeredName"
                placeholder="Enter Registered Name"
                {...register("registeredName")}
              />
            </div>
            <div>
              <UploadFormRow
                defaultImage={defaultImage && defaultImage}
                preview={true}
                previewSize={"113px"}
                name="photo"
                label={"Horse photo"}
                className={"create-account-form-fields__field"}
                compact
                immediateUpload
              ></UploadFormRow>
            </div>
          </div>
          <div className="flex !gap-x-5 sm:!gap-[18px] horse-form-actions">
            <Button
              loading={isLoading}
              type={"submit"}
              className={
                "horse-add-save-btn !w-[150px] !h-[56px] sm:!w-[182px]"
              }
              primary
              whiteOutline
            >
              save
            </Button>
            <Button
              type="button"
              onClick={onCancel}
              className={
                "horse-add-cancel-btn !w-[150px] !h-[56px] sm:!w-[182px]"
              }
              primaryOutline
            >
              cancel
            </Button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

export default HorseForm;
